import { Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { CookieService, SessionService, Snackbar } from '@le_cedre/portal-lib';
import Cookie from 'js-cookie';
import { BehaviorSubject, Observable } from 'rxjs';
import { IloginByEmailRequestPayload, LoginProvider } from './login.provider';
import { SNACKBAR_USERS_ID_OR_PASSWORD_ERROR } from '@app/shared/utils/snackbar.utils';

export interface TLoginState {
    maximumFailedLoginAttempts: number;
    currentFailedLoginAttempts: number;
}
export const defaultState: TLoginState = {
    maximumFailedLoginAttempts: 3,
    currentFailedLoginAttempts: 0,
};

@Injectable({
    providedIn: 'root',
})
export class LoginService {
    isLogging = signal(false);

    private state$ = new BehaviorSubject(defaultState);

    private get state() {
        return this.state$.value;
    }

    all$(): Observable<TLoginState> {
        return this.state$.asObservable();
    }

    constructor(
        private loginProvider: LoginProvider,
        private cookieService: CookieService,
        private session: SessionService,
        private snackbar: Snackbar
    ) {}

    async requestLoginByEmail(payload: IloginByEmailRequestPayload, isCollab = false) {
        try {
            const response = await this.loginProvider.lrLogin(payload);
            this.cookieService.setRefreshToken({ refreshToken: response.refresh_token });

            if (isCollab) {
                this.loginByEmailCollabSuccess(response.Profile.Uid, response.access_token);
            } else {
                this.session.fetchProfileByToken(response.access_token, response.Profile.Uid);
                this.isLogging.set(false);
            }

            this.patchState({ currentFailedLoginAttempts: 0 });
        } catch (error) {
            this.patchState({ currentFailedLoginAttempts: ++this.state.currentFailedLoginAttempts });
            this.snackbar.createSnackbarFromError(SNACKBAR_USERS_ID_OR_PASSWORD_ERROR);
            this.isLogging.set(false);
        }
    }

    requestLoginAsCollab(payload: { email: string; accessToken: string; sessionUid: string; refreshToken: string }) {
        const { domain } = environment;
        Cookie.set('collab_email', payload.email, { domain });
        this.session.fetchProfileByToken(payload.accessToken, payload.sessionUid, true);
        this.cookieService.setRefreshToken({ refreshToken: payload.refreshToken });
    }

    async requestLoginSSOAsCollab(authToken: string) {
        const response = await this.loginProvider.googleAuth(authToken);
        this.session.fetchUidProfileByToken(response.access_token, response.refresh_token);
    }

    private async loginByEmailCollabSuccess(uid, accessToken) {
        try {
            const response = await this.loginProvider.refresh(uid, accessToken);
            this.session.setCollab(response);
            this.cookieService.setToken({
                accessToken: response.access_token,
                accessTokenDe: response.access_token_de,
                accessTokenEa: response.access_token_ea,
                accessTokenEf: response.access_token_ef,
                uId: response.uid,
            });
            this.isLogging.set(false);
        } catch (error) {
            this.session.failFetchProfile(error);
            this.cookieService.requestDeleteToken({ redirect: true });
        }
    }

    private patchState(data: Partial<TLoginState>) {
        const state = this.state$.value;
        this.state$.next({
            ...state,
            ...data,
        });
    }
}
